﻿import { useRef, useState } from 'react';
import { Outlet } from 'react-router';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import useSettings from '../../hooks/useSettings';
import useResponsive from '../../hooks/useResponsive';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
import { HEADER, NAVBAR } from '../../config';
import DashboardHeader from './header';
import NavbarVertical from './navbar/NavbarVertical';
import NavbarHorizontal from './navbar/NavbarHorizontal';
import { m } from 'framer-motion';
import useScrolling from 'src/hooks/useScrolling';
import useWindowDimensions from 'src/hooks/useWindowDimensionHook';
import ImpersonationBanner from './header/ImpersonationBanner';
import useImpersonateContext from 'src/hooks/useImpersonateContext';

// ----------------------------------------------------------------------

type MainStyleProps = {
  collapseClick: boolean;
};

const MainStyle = styled('main', {
  shouldForwardProp: (prop) => prop !== 'collapseClick',
})<MainStyleProps>(({ collapseClick, theme }) => ({
  flexGrow: 1,
  minHeight: '100vh',
  backgroundColor: theme.palette.customBackground.main,
  [theme.breakpoints.up('lg')]: {
    paddingLeft: 0,
    paddingRight: 0,
    transition: theme.transitions.create('margin-left', {
      duration: theme.transitions.duration.shorter,
    }),
    ...(collapseClick && { marginLeft: NAVBAR.DASHBOARD_COLLAPSE_WIDTH }),
    ...(!collapseClick && { marginLeft: NAVBAR.DASHBOARD_WIDTH }),
  },
}));

const HEADER_EXPANDED_HEIGHT =
  HEADER.DASHBOARD_DESKTOP_BANNER_HEIGHT + HEADER.DASHBOARD_DESKTOP_UNDER_BANNER_HEIGHT;

const HEADER_COLLAPSED_HEIGHT = HEADER.DASHBOARD_DESKTOP_BANNER_HEIGHT_COLLAPSED;

// ----------------------------------------------------------------------

export default function DashboardLayout() {
  const { collapseClick, isCollapse } = useCollapseDrawer();
  const { themeLayout } = useSettings();
  const { height } = useWindowDimensions();

  const isDesktop = useResponsive('up', 'lg');

  const { headerCollapsed } = useScrolling();

  const { bannerOffset } = useImpersonateContext();

  const mainBoxRef = useRef<any>();

  const [open, setOpen] = useState(false);
  const verticalLayout = themeLayout === 'vertical';

  if (verticalLayout) {
    const verticalTarget = {
      paddingTop: headerCollapsed ? HEADER_COLLAPSED_HEIGHT : HEADER_EXPANDED_HEIGHT,
    };

    return (
      <>
        <ImpersonationBanner />

        <DashboardHeader onOpenSidebar={() => setOpen(true)} verticalLayout={verticalLayout} />

        {isDesktop ? (
          <NavbarHorizontal />
        ) : (
          <NavbarVertical isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />
        )}

        <m.div
          initial={false}
          animate={verticalTarget}
          transition={{ duration: 0.1 }}
          ref={mainBoxRef}
        >
          <Box component="main" sx={{ px: { lg: 2 } }}>
            <Outlet />
          </Box>
        </m.div>
      </>
    );
  }

  const mainTarget = headerCollapsed
    ? {
        paddingTop: HEADER_COLLAPSED_HEIGHT,
        height: height - HEADER_COLLAPSED_HEIGHT - bannerOffset,
      }
    : {
        paddingTop: HEADER_EXPANDED_HEIGHT,
        height: height - bannerOffset,
      };

  return (
    <>
      <ImpersonationBanner />

      <DashboardHeader isCollapse={isCollapse} onOpenSidebar={() => setOpen(true)} />

      <NavbarVertical isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />

      <m.div
        initial={false}
        animate={mainTarget}
        transition={{ duration: 0.1 }}
        ref={mainBoxRef}
        style={{ width: '100%' }}
      >
        <MainStyle collapseClick={collapseClick}>
          <Outlet />
        </MainStyle>
      </m.div>
    </>
  );
}